import { Box } from "@radix-ui/themes";
import { Treemap, ResponsiveContainer } from "recharts";

interface Category {
  name: string;
  description: string;
  count: number;
  color: string;
}

interface CategoryHeatMapProps {
  categories: Category[];
  height?: number;
}

interface TreemapNode {
  name: string;
  size: number;
  fill: string;
  [key: string]: string | number;
}

// Custom content component for treemap cells
const CustomizedContent = (props: any) => {
  const { x, y, width, height, name, fill } = props;

  // Function to wrap text into multiple lines
  const wrapText = (text: string, maxWidth: number): string[] => {
    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      // Rough estimate: 8px per character
      if (testLine.length * 8 > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  };

  const shouldShowText = width > 80 && height > 30;
  const lines = shouldShowText ? wrapText(name, width - 10) : [];
  const lineHeight = 16;
  const totalTextHeight = lines.length * lineHeight;
  const startY = y + height / 2 - totalTextHeight / 2 + lineHeight / 2;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill,
          stroke: "#fff",
          strokeWidth: 2,
        }}
      />
      {shouldShowText &&
        lines.map((line, index) => (
          <text
            key={index}
            x={x + width / 2}
            y={startY + index * lineHeight}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#fff"
            fontSize={14}
            fontWeight="bold"
            stroke="none"
            style={{
              textShadow: "0 1px 2px rgba(0,0,0,0.3)",
            }}
          >
            {line}
          </text>
        ))}
    </g>
  );
};

export function CategoryHeatMap({
  categories,
  height = 300,
}: CategoryHeatMapProps) {
  // Transform categories to treemap data format
  const data: TreemapNode[] = categories.map((cat) => ({
    name: cat.name,
    size: cat.count,
    fill: cat.color,
  }));

  return (
    <Box style={{ width: "100%", height: `${height}px` }}>
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={data}
          dataKey="size"
          stroke="#fff"
          fill="#8884d8"
          content={<CustomizedContent />}
          isAnimationActive={false}
        />
      </ResponsiveContainer>
    </Box>
  );
}
